import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { 
  ShoppingBag, 
  Truck, 
  CreditCard, 
  CheckCircle, 
  MapPin,
  Mail,
  AlertCircle,
  X
} from 'lucide-react';
import { 
  // getAllCities, 
  getDeliveryCharge, 
  searchCities,
  isCityServiced 
} from '../utils/deliveryLocations';
import axios from 'axios';

const CheckoutPage = () => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddress: '',
    shippingCity: '',
    shippingPostalCode: '',
    specialInstructions: '',
    deliveryDate: ''
  });

  // UI states
  const [deliveryCharge, setDeliveryCharge] = useState(null);
  const [citySearch, setCitySearch] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (cartItems.length === 0 && !isCheckingOut) {
      navigate('/products');
    }
  }, [cartItems, navigate, isCheckingOut]);

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => {
    return sum + (parseFloat(item.priceCAD) * item.quantity);
  }, 0);

  const total = deliveryCharge !== null ? subtotal + deliveryCharge : subtotal;

  // Handle city search
  const handleCitySearch = (value) => {
    setCitySearch(value);
    setFormData({ ...formData, shippingCity: value });
    
    if (value.length >= 1) {
      const suggestions = searchCities(value);
      setCitySuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setCitySuggestions([]);
      setShowSuggestions(false);
    }

    // Update delivery charge
    const charge = getDeliveryCharge(value);
    setDeliveryCharge(charge);
  };

  // Select city from suggestions
  const selectCity = (city) => {
    setCitySearch(city);
    setFormData({ ...formData, shippingCity: city });
    setShowSuggestions(false);
    
    const charge = getDeliveryCharge(city);
    setDeliveryCharge(charge);
    
    // Clear city error if exists
    if (errors.shippingCity) {
      setErrors({ ...errors, shippingCity: '' });
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Name is required';
    }

    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Invalid email address';
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (!/^[0-9\s\-+()]+$/.test(formData.customerPhone)) {
      newErrors.customerPhone = 'Invalid phone number';
    }

    if (!formData.shippingAddress.trim()) {
      newErrors.shippingAddress = 'Delivery address is required';
    }

    if (!formData.shippingCity.trim()) {
      newErrors.shippingCity = 'City is required';
    } else if (!isCityServiced(formData.shippingCity)) {
      newErrors.shippingCity = 'Sorry, we don\'t deliver to this city';
    }
    if(!formData.shippingPostalCode.trim()){
      newErrors.shippingPostalCode = 'Postal code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setIsCheckingOut(true);
    setSubmitError('');

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const orderData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        shippingAddress: formData.shippingAddress,
        shippingCity: formData.shippingCity,
        shippingPostalCode: formData.shippingPostalCode,
        cartItems: cartItems,
        specialInstructions: formData.specialInstructions,
        deliveryDate: formData.deliveryDate || null
      };

      const response = await axios.post(`${API_URL}/orders`, orderData);

      if (response.data.success) {
        // Clear cart
        clearCart();
        // Redirect to confirmation page
        navigate(`/order-confirmation/${response.data.data.orderNumber}`);
      }

    } catch (error) {
      console.error('Order submission error:', error);
      setSubmitError(
        error.response?.data?.message || 
        'Failed to place order. Please try again or contact us for assistance.'
      );
      setIsCheckingOut(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 py-12">
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-900 mb-2">
            Checkout
          </h1>
          <p className="text-lg text-dark-600">
            Complete your order - Cash on Delivery
          </p>
        </div>

        {/* Error Alert */}
        {submitError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-semibold">Order Failed</p>
              <p className="text-red-700 text-sm">{submitError}</p>
            </div>
            <button onClick={() => setSubmitError('')} className="text-red-600 hover:text-red-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Contact Information */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-dark-900">
                    Contact Information
                  </h2>
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="Write Your Name"
                      required
                      aria-required="true"
                      className={`w-full px-4 py-3 border ${errors.customerName ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`}
                    />
                    {errors.customerName && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      placeholder="xyz@example.com"
                      required
                      aria-required="true"
                      className={`w-full px-4 py-3 border ${errors.customerEmail ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`}
                    />
                    {errors.customerEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerEmail}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      placeholder="+1 (416) 555-0123"
                      required
                      aria-required="true"
                      className={`w-full px-4 py-3 border ${errors.customerPhone ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`}
                    />
                    {errors.customerPhone && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerPhone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-accent-600" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-dark-900">
                    Delivery Address
                  </h2>
                </div>

                <div className="space-y-4">
                  {/* Address */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="shippingAddress"
                      value={formData.shippingAddress}
                      onChange={handleChange}
                      placeholder="123 Main Street, Apt 4B"
                      required
                      aria-required="true"
                      className={`w-full px-4 py-3 border ${errors.shippingAddress ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`}
                    />
                    {errors.shippingAddress && (
                      <p className="mt-1 text-sm text-red-600">{errors.shippingAddress}</p>
                    )}
                  </div>

                  {/* City with Autocomplete */}
                  <div className="relative">
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={citySearch}
                        onChange={(e) => handleCitySearch(e.target.value)}
                        onFocus={() => citySearch.length >= 1 && setShowSuggestions(true)}
                        placeholder="Start typing city name..."
                        className={`w-full px-4 py-3 border ${errors.shippingCity ? 'border-red-500' : 'border-gray-300'} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`}
                      />
                      <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    
                    {/* City Suggestions Dropdown */}
                    {showSuggestions && citySuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {citySuggestions.map((city, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectCity(city)}
                            className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-dark-900">{city}</span>
                              <span className="text-sm text-primary-600 font-semibold">
                                ${getDeliveryCharge(city)} delivery
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {errors.shippingCity && (
                      <p className="mt-1 text-sm text-red-600">{errors.shippingCity}</p>
                    )}
                    
                    {/* Delivery Charge Indicator */}
                    {deliveryCharge !== null && !errors.shippingCity && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-700">
                          Delivery available for <strong>${deliveryCharge} CAD</strong>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="shippingPostalCode"
                      value={formData.shippingPostalCode}
                      onChange={handleChange}
                      placeholder="M5V 2T6"
                      required
                      aria-required="true"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                    />
                  </div>

                  {/* Delivery Date */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Preferred Delivery Date (Optional)
                    </label>
                    <input
                      type="date"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                    />
                  </div>

                  {/* Special Instructions */}
                  <div>
                    <label className="block text-sm font-semibold text-dark-700 mb-2">
                      Special Instructions (Optional)
                    </label>
                    <textarea
                      name="specialInstructions"
                      value={formData.specialInstructions}
                      onChange={handleChange}
                      placeholder="Any special delivery instructions, floor number, gate code, etc."
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-dark-900">
                    Payment Method
                  </h2>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">💰</div>
                    <div>
                      <h3 className="font-bold text-dark-900 text-lg mb-2">
                        Cash on Delivery (COD)
                      </h3>
                      <p className="text-dark-600 mb-3">
                        Pay with cash when your order is delivered. Please have the exact amount ready.
                      </p>
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <p className="text-sm text-dark-600 mb-1">Amount to pay at delivery:</p>
                        <p className="text-2xl font-bold text-green-600">
                          ${total.toFixed(2)} CAD
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || deliveryCharge === null}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                  isSubmitting || deliveryCharge === null
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-700 hover:to-accent-700 text-white shadow-xl hover:shadow-2xl transform hover:scale-[1.02]'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                    Processing Order...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6" />
                    Place Order - ${total.toFixed(2)} CAD
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              
              <div className="flex items-center gap-3 mb-6">
                <ShoppingBag className="w-6 h-6 text-primary-600" />
                <h2 className="text-xl font-display font-bold text-dark-900">
                  Order Summary
                </h2>
              </div>

              {/* Cart Items */}
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.cartItemId || item.id} className="flex gap-4 pb-4 border-b">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-dark-900 mb-1 text-sm">
                        {item.name}
                      </h3>
                      
                      {/* Options Container - Text Only */}
                      <div className="space-y-1 mb-1">
                        {/* Color Display - Text Only */}
                        {item.options?.color && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-dark-600 font-medium">
                              {item.options.color}
                            </span>
                          </div>
                        )}
                        
                        {/* Handle Display */}
                        {item.options?.handle && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-blue-700 font-medium bg-blue-50 px-2 py-0.5 rounded">
                              Handle {item.options.handle}
                            </span>
                          </div>
                        )}
                        
                        {/* Dimensions Display */}
                        {item.dimensions && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-green-700 font-medium bg-green-50 px-2 py-0.5 rounded">
                              {item.dimensions.height} × {item.dimensions.width} × {item.dimensions.depth}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm text-dark-500">
                        Qty: {item.quantity}
                      </p>
                      <p className="text-sm font-bold text-primary-600">
                        ${(item.priceCAD * item.quantity).toFixed(2)} CAD
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.cartItemId || item.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex justify-between text-dark-600">
                  <span>Subtotal:</span>
                  <span className="font-semibold">${subtotal.toFixed(2)} CAD</span>
                </div>
                
                <div className="flex justify-between text-dark-600">
                  <span>Delivery:</span>
                  <span className="font-semibold">
                    {deliveryCharge !== null ? `$${deliveryCharge.toFixed(2)} CAD` : 'Select city'}
                  </span>
                </div>
                
                <div className="flex justify-between text-xl font-bold text-dark-900 pt-3 border-t">
                  <span>Total:</span>
                  <span className="text-primary-600">
                    ${total.toFixed(2)} CAD
                  </span>
                </div>
              </div>

              {/* Delivery Info */}
              <div className="mt-6 bg-primary-50 rounded-xl p-4">
                <p className="text-xs text-primary-700">
                 🚚 Delivery within 2 working days to most locations
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;